// app/auth/components/AuthRecoveryRedirect.tsx
"use client";

import type { AuthChangeEvent } from "@supabase/supabase-js";
import { usePathname, useRouter } from "next/navigation";
import { useEffect } from "react";

import { createClient } from "@/utils/supabase/client";

const UPDATE_PASSWORD_PATH = "/auth/update-password";

function hashIndicatesRecovery(hash: string): boolean {
  const normalized = hash.startsWith("#") ? hash.slice(1) : hash;
  const params = new URLSearchParams(normalized);
  return params.get("type") === "recovery";
}

/**
 * Safety net for password-recovery links.
 *
 * When Supabase rejects an un-allowlisted redirectTo it falls back to Site URL
 * (`http://howthef.ai` today) with tokens in the hash. This component detects
 * that recovery hash (or the PASSWORD_RECOVERY auth event) on any route and
 * sends the user to the update-password page with the hash intact.
 */
export function AuthRecoveryRedirect() {
  const pathname = usePathname();
  const router = useRouter();

  useEffect(() => {
    if (typeof window === "undefined") return;

    const sendToUpdatePassword = () => {
      if (pathname?.startsWith(UPDATE_PASSWORD_PATH)) return;
      const hash = window.location.hash || "";
      router.replace(`${UPDATE_PASSWORD_PATH}${hash}`);
    };

    if (hashIndicatesRecovery(window.location.hash || "")) {
      sendToUpdatePassword();
      return;
    }

    let subscription: { unsubscribe: () => void } | undefined;
    try {
      const supabase = createClient();
      const { data } = supabase.auth.onAuthStateChange(
        (event: AuthChangeEvent) => {
          if (event === "PASSWORD_RECOVERY") {
            sendToUpdatePassword();
          }
        },
      );
      subscription = data.subscription;
    } catch (error) {
      console.error("[AuthRecoveryRedirect] Failed to attach listener:", error);
    }

    return () => {
      subscription?.unsubscribe();
    };
  }, [pathname, router]);

  return null;
}
